<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <router-link :to="{ name: 'ChatMateConversations' }" class="btn btn-secondary">
        <i class="ri-arrow-left-line"></i>
        返回列表
      </router-link>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchConversation">重試</button>
    </div>

    <div v-else-if="conversationData" class="conversation-detail">
      <div class="detail-section">
        <h2>會話資訊</h2>
        <dl class="detail-list">
          <dt>會話 ID</dt>
          <dd>{{ conversationData.sessionId }}</dd>
          
          <dt>總訊息數</dt>
          <dd>{{ conversationData.stats?.total_messages || 0 }}</dd>
          
          <dt>總成本（台幣）</dt>
          <dd>{{ formatTwdFromStored(conversationData.stats?.total_cost_twd || 0) }}</dd>
          
          <dt>平均信心度</dt>
          <dd>
            <span v-if="conversationData.stats?.avg_confidence" class="badge badge-info">
              {{ (parseFloat(conversationData.stats.avg_confidence) * 100).toFixed(0) }}%
            </span>
            <span v-else>-</span>
          </dd>
          
          <dt>首次訊息時間</dt>
          <dd>{{ formatDate(conversationData.stats?.first_message_at) }}</dd>
          
          <dt>最後訊息時間</dt>
          <dd>{{ formatDate(conversationData.stats?.last_message_at) }}</dd>
        </dl>
      </div>

      <div class="detail-section">
        <h2>對話記錄</h2>
        <div class="conversation-messages">
          <template v-for="(msg, index) in conversationData.conversations" :key="`msg-${index}`">
            <!-- 用戶訊息 -->
            <div
              v-if="msg.user_message"
              class="message-item user-message"
            >
              <div class="message-header">
                <span class="message-role">用戶</span>
              </div>
              <div class="message-content">
                {{ msg.user_message }}
              </div>
              <div class="message-footer">
                {{ formatDate(msg.created_at) }}
              </div>
            </div>
            <!-- AI 回覆 -->
            <div
              v-if="msg.ai_reply"
              class="message-item ai-message"
            >
              <div class="message-header">
                <span class="message-role">AI 助理</span>
                <span v-if="msg.confidence != null" class="message-confidence">
                  信心度: {{ (parseFloat(msg.confidence) * 100).toFixed(0) }}%
                </span>
                <span v-if="msg.cost_usd" class="message-cost">
                  成本: {{ formatTwdFromStored(msg.cost_twd || 0) }}
                </span>
              </div>
              <div class="message-content">
                {{ msg.ai_reply }}
              </div>
              <div class="message-footer">
                {{ formatDate(msg.created_at) }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError } from '@/utils/swal.js';
import { formatTwdFromStored } from '@/utils/currency.js';

export default {
  name: 'ChatMateConversationDetail',
  setup() {
    const { pageTitle } = usePageTitle('對話詳情');
    const route = useRoute();
    const conversationData = ref(null);
    const loading = ref(false);
    const error = ref(null);

    const fetchConversation = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await chatmateService.getConversation(route.params.sessionId);

        if (response && response.success && response.data) {
          conversationData.value = response.data;
        } else {
          error.value = response?.message || '獲取對話詳情失敗';
        }
      } catch (err) {
        console.error('獲取對話詳情失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取對話詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      // 顯示更詳細的時間，包含秒數，讓用戶能區分不同時間的對話
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    onMounted(() => {
      fetchConversation();
    });

    return {
      pageTitle,
      conversationData,
      loading,
      error,
      fetchConversation,
      formatDate,
      formatTwdFromStored
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.conversation-detail {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.detail-section {
  margin-bottom: 32px;
}

.detail-section h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.detail-list {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 16px;
}

.detail-list dt {
  font-weight: 600;
  color: #666;
}

.detail-list dd {
  margin: 0;
  color: #333;
}

.conversation-messages {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-item {
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid;
}

.user-message {
  background-color: #f0f8ff;
  border-left-color: #007bff;
}

.ai-message {
  background-color: #f5f5f5;
  border-left-color: #28a745;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 14px;
}

.message-role {
  font-weight: 600;
  color: #333;
}

.message-confidence,
.message-cost {
  color: #666;
  font-size: 12px;
}

.message-content {
  color: #333;
  line-height: 1.6;
  margin-bottom: 8px;
}

.message-footer {
  font-size: 12px;
  color: #999;
}
</style>
